/** Collector + Culture Member progression — tiers, missions, hierarchy, perks. */

export type CollectorTier = 'bronze' | 'silver' | 'gold' | 'platinum' | 'black_card';

export type DistrictPath =
  | 'visitor'
  | 'member'
  | 'collector'
  | 'creator'
  | 'seller'
  | 'operator'
  | 'partner'
  | 'staff';

export type ShowcaseType =
  | 'sneaker_wall'
  | 'outfit_gallery'
  | 'grail_collection'
  | 'favorite_pickups';

export const DISTRICT_PATH_LADDER: { path: DistrictPath; label: string; description: string }[] = [
  { path: 'visitor', label: 'Visitor', description: 'Browsing the district' },
  { path: 'member', label: 'Member', description: 'Account active — culture awaits' },
  { path: 'collector', label: 'Collector', description: 'Building a vault & collection' },
  { path: 'creator', label: 'Creator', description: 'Publishing culture & growing audience' },
  { path: 'seller', label: 'Seller', description: 'Monetizing inventory & storefront' },
  { path: 'operator', label: 'Operator', description: 'Ambassador / Nexus operations' },
  { path: 'partner', label: 'Partner Brand', description: 'Official brand partnerships' },
  { path: 'staff', label: 'District88 Staff', description: 'Platform operations' },
];

export const COLLECTOR_TIERS: Record<
  CollectorTier,
  { label: string; minScore: number; color: string; perks: string[] }
> = {
  bronze: {
    label: 'Bronze Collector',
    minScore: 0,
    color: 'text-orange-400',
    perks: ['Collection vault', 'Grail wishlist', 'Community badges'],
  },
  silver: {
    label: 'Silver Collector',
    minScore: 300,
    color: 'text-zinc-300',
    perks: ['Priority support', 'Early drop alerts', 'Showcase galleries'],
  },
  gold: {
    label: 'Gold Collector',
    minScore: 1000,
    color: 'text-amber-400',
    perks: ['Reduced marketplace fees', 'Exclusive drops access', 'Grail hunt priority'],
  },
  platinum: {
    label: 'Platinum Collector',
    minScore: 2500,
    color: 'text-purple-400',
    perks: ['VIP drop access', 'Bounty board boosts', 'Collector league entry'],
  },
  black_card: {
    label: '88 Black Card Collector',
    minScore: 5000,
    color: 'text-red-400',
    perks: ['Maximum perks', 'Concierge sourcing', 'Black Card badge on profile'],
  },
};

export const COLLECTOR_SCORE_SOURCES = [
  { label: 'Purchases', detail: '120 pts each' },
  { label: 'Collection value', detail: '1 pt per $10 in vault' },
  { label: 'Vault items', detail: '25 pts each' },
  { label: 'Reviews', detail: '40 pts each' },
  { label: 'Community posts', detail: '15 pts each' },
  { label: 'Dream grails', detail: '10 pts each active' },
  { label: 'Passport stamps', detail: '30 pts each' },
  { label: 'Trend Hunter', detail: 'Points from early saves' },
  { label: 'Login streak', detail: 'Up to 200 bonus pts' },
];

export const COLLECTOR_ACHIEVEMENTS = [
  { key: 'first_purchase', title: 'First Purchase', emoji: '🛍️', description: 'Complete your first order' },
  { key: 'ten_purchases', title: '10 Purchases', emoji: '🔟', description: 'Ten orders in the books' },
  { key: 'spent_1k', title: 'Spent $1,000', emoji: '💵', description: 'Lifetime spend milestone' },
  { key: 'first_verified', title: 'First Verified Item', emoji: '✅', description: 'Own an authenticated piece' },
  { key: 'first_review', title: 'First Review', emoji: '⭐', description: 'Leave your first review' },
  { key: 'streak_100', title: '100 Day Streak', emoji: '🔥', description: '100 consecutive days active' },
  { key: 'community_og', title: 'Community OG', emoji: '🏛️', description: '365 days in the district' },
  { key: 'grail_found', title: 'Grail Secured', emoji: '👑', description: 'Find a dream grail' },
  { key: 'showcase_published', title: 'Showcase Live', emoji: '🖼️', description: 'Publish a collection showcase' },
  { key: 'trend_spotter', title: 'Trend Spotter', emoji: '🎯', description: 'Save something before it trends' },
] as const;

export const CULTURE_DAILY_MISSIONS = [
  { key: 'watch_videos', label: 'Watch 3 videos', xp: 15, target: 3 },
  { key: 'leave_comments', label: 'Leave 2 comments', xp: 12, target: 2 },
  { key: 'save_item', label: 'Save 1 item', xp: 10, target: 1 },
  { key: 'follow_creators', label: 'Follow 3 creators', xp: 15, target: 3 },
  { key: 'read_article', label: 'Read 1 article', xp: 8, target: 1 },
  { key: 'browse_drops', label: 'Browse drops', xp: 10, target: 1 },
] as const;

export const SHOWCASE_TYPES: { type: ShowcaseType; label: string; icon: string }[] = [
  { type: 'sneaker_wall', label: 'Sneaker Wall', icon: '👟' },
  { type: 'outfit_gallery', label: 'Outfit Gallery', icon: '👔' },
  { type: 'grail_collection', label: 'Grail Collection', icon: '👑' },
  { type: 'favorite_pickups', label: 'Favorite Pickups', icon: '🔥' },
];

export function collectorTierForScore(score: number): CollectorTier {
  if (score >= 5000) return 'black_card';
  if (score >= 2500) return 'platinum';
  if (score >= 1000) return 'gold';
  if (score >= 300) return 'silver';
  return 'bronze';
}

export function collectorTierProgress(score: number): { tier: CollectorTier; nextTier: CollectorTier | null; percent: number } {
  const tier = collectorTierForScore(score);
  const tiers: CollectorTier[] = ['bronze', 'silver', 'gold', 'platinum', 'black_card'];
  const idx = tiers.indexOf(tier);
  const nextTier = idx < tiers.length - 1 ? tiers[idx + 1] : null;
  const currentMin = COLLECTOR_TIERS[tier].minScore;
  const nextMin = nextTier ? COLLECTOR_TIERS[nextTier].minScore : COLLECTOR_TIERS.black_card.minScore;
  const percent = nextTier
    ? Math.min(100, Math.round(((score - currentMin) / (nextMin - currentMin)) * 100))
    : 100;
  return { tier, nextTier, percent };
}

export function cultureLevelProgress(xp: number): { level: number; percent: number } {
  const level = Math.min(88, Math.max(1, 1 + Math.floor(xp / 250)));
  const levelXp = (level - 1) * 250;
  const nextXp = level * 250;
  const percent = level >= 88 ? 100 : Math.min(100, Math.round(((xp - levelXp) / (nextXp - levelXp)) * 100));
  return { level, percent };
}

export function formatCollectorTier(tier: string): string {
  return COLLECTOR_TIERS[tier as CollectorTier]?.label ?? 'Collector';
}

export function formatDistrictPath(path: string): string {
  return DISTRICT_PATH_LADDER.find((p) => p.path === path)?.label ?? 'Member';
}
